import { useState } from 'react';
import { AuthError, createAuth, type KilroySession } from '@kilroy/shared';
import './signin.css';

/* Signing in.
 *
 * The first screen anyone sees, and on a field app it carries an unusual
 * promise: sign in once with signal and the phone keeps working all day
 * without it. That is stated here rather than discovered, because someone
 * standing at a trailhead deciding whether to bother is exactly who needs to
 * know it.
 *
 * Deliberately not a wall. One mark, one heading, two fields. The mark is the
 * stratigraphic column from the rail — copper band, dark band, hairline, stone
 * — which is the only ornament on the screen and the only one it needs.
 */

export function SignIn({ onSignedIn }: { onSignedIn: (s: KilroySession) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);

  const url = import.meta.env.VITE_SUPABASE_URL ?? '';
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
  const configured = Boolean(url && anonKey);

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setOffline(false);
    try {
      const session = await createAuth({ url, anonKey }).signIn(email, password);
      onSignedIn(session);
    } catch (err) {
      const e = err as AuthError;
      setError(e.message);
      setOffline(Boolean(e.offline));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <div className="signin__box">
        <div className="signin__brand">
          <span className="signin__mark" aria-hidden="true" />
          <span className="signin__wordmark">Kilroy</span>
          <span className="signin__sub mono">Field</span>
        </div>

        <h1 className="signin__t">Sign in once.</h1>
        <p className="signin__s">
          Then the phone works all day with no signal. Everything you capture saves here the
          moment you tap Save — the network is something that happens later.
        </p>

        {!configured ? (
          <p className="signin__err">
            Sign-in is not configured on this build. VITE_SUPABASE_URL and
            VITE_SUPABASE_ANON_KEY are missing.
          </p>
        ) : (
          <form className="signin__form" onSubmit={submit}>
            <div className="field">
              <label className="label" htmlFor="si-e">
                Email
              </label>
              <input
                id="si-e"
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                required
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="si-p">
                Password
              </label>
              <input
                id="si-p"
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <p className={`signin__err${offline ? ' signin__err--offline' : ''}`}>{error}</p>
            )}

            <button type="submit" className="btn btn--primary btn--lg signin__go" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        {/* The pilot accounts, on the screen.
         *
         * A demo that stalls because nobody can remember the password is a
         * demo that failed for the wrong reason. These are seven fictional
         * people whose data is entirely seeded and marked is_demo — there is
         * nothing here to protect, and pretending otherwise would cost more
         * than it saves. */}
        <details className="signin__demo">
          <summary className="mono">Pilot accounts</summary>
          <p className="mono signin__demo-b">
            thomas@kilroymetal.example
            <br />
            allan@kilroymetal.example
            <br />
            brandon@kilroymetal.example
            <br />
            <span className="signin__pw">KilroyField2026!</span>
          </p>
        </details>
      </div>

      <p className="signin__foot mono">Kilroy Metal · Blue Bell &amp; DeSoto · Arizona</p>
    </div>
  );
}
